import StatusChip from 'components/@extended/StatusChip';
import AuditInfo from 'components/audit/AuditInfo';
import { OFF_SECONDARY_CLASS } from 'components/buttons/buttonStyle';
import PermissionButton from 'components/buttons/PermissionButton';
import PinButton from 'components/buttons/PinButton';
import DmsLink, { dmsLinkValue } from 'components/dmslink/DmsLink';
import { DraftChip } from 'components/drafts/DraftChip';
import { DraftNotice } from 'components/drafts/DraftNotice';
import { DraftMeta, DraftRecordActions } from 'components/drafts/DraftRecordActions';
import { DraftSharedWith } from 'components/drafts/DraftSharedWith';
import { DraftActionsContext, useDraftActions } from 'components/drafts/useDraftActions';
import { withMissing } from 'components/drafts/withMissing';
import GridItemList, { infoLabel, Row } from 'components/GridItemList';
import Loader from 'components/Loader';
import { EllipsisTooltip } from 'components/misc/EllipsisTooltip';
import { FormattedDate } from 'components/misc/FormattedDate';
import PageHeader from 'components/PageHeader';
import PartyName from 'components/party/PartyName';
import { SectionEditProvider } from 'components/section-edit/SectionEdit';
import useAuth from 'hooks/useAuth';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import { DebriefingMin } from 'pages/debriefing/types';
import { getCaseChips, getLinkChips, getLinks } from 'pages/matters/util';
import { FormattedMessage } from 'react-intl';
import { RecordLink } from 'components/record-chip/RecordLink';
import { Link as RouterLink } from 'react-router-dom';
import { useSelector } from 'store';
import { useSwitchTasksStatusMutation } from 'store/api/tasks';
import { FeatureFlag } from 'types/features';
import { getPriorityStatusChipType, Task, TaskStatusType } from 'types/tasks';
import { formatTimeOfDay, isDateEarlierThanToday } from 'utils/date';
import { getOptionsFromClassifier } from 'utils/formUtil';
import { getClassifierText, t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { recordPin } from 'utils/pins';
import { sortByField } from 'utils/sort';
import { cn } from 'utils/cn';
import { useClassifiers } from 'utils/useClassifiers';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { useGoBack, useLeaveDeletedEntity, useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import { TaskSection } from './form-sections/TaskSection';
import { TaskSectionId, canEditTaskInPlace } from './taskFormMapping';

type Props = {
    task: Task;
};

const TASK_UPDATE_PERMISSIONS = ['UPDATE.TASKS.ALL', 'UPDATE.TASKS.MY_OWN', 'UPDATE.TASKS.CREATED_BY_ME'];
// The in-place edit gate splits these: UPDATE.TASKS.CREATED_BY_ME lets in only the task's creator.
const TASK_UPDATE_ANY_PERMISSIONS = ['UPDATE.TASKS.ALL', 'UPDATE.TASKS.MY_OWN'];
const TASK_UPDATE_CREATED_PERMISSIONS = ['UPDATE.TASKS.CREATED_BY_ME'];

// `entity-link` lets a `GridItemList` row give the link its own colour.
const LINK_CLASS = 'entity-link text-fg hover:text-link w-fit no-underline hover:underline';

const debriefingLabel = (debriefing: DebriefingMin): string => {
    const entity = debriefing.clientParty?.name ?? debriefing.projectName ?? '';
    return [entity, debriefing.debriefingDate].filter(Boolean).join(' · ');
};

const TaskPageView = ({ task }: Props): JSX.Element => {
    const { classifiers, isLoading } = useClassifiers({
        codes: ['TASK_PRIORITY_TYPE', 'TASK_TYPE', 'TASK_STATUS_TYPE', 'EVENT_REMINDER_CODE', 'DMS_DATABASE'],
    });
    const navigateWithReturn = useNavigateWithReturnState();
    const goBack = useGoBack('/tasks');
    const [switchTaskStatus] = useSwitchTasksStatusMutation();
    // The switch-status endpoint toggles, so a second click mid-request would flip the status back.
    const { buttonsDisabled } = useSelector((state) => state.buttons);

    const canReadAssignments = useHasPermissions(['READ.MATTER_ASSIGNMENTS']);
    const isAssignmentsEnabled = useIsFeatureEnabled(FeatureFlag.CASE_ASSIGNMENT_ENABLED);
    const showAssignments = canReadAssignments && isAssignmentsEnabled;
    const showCorrespondence = useIsFeatureEnabled(FeatureFlag.CORRESPONDENCE_MODULE);
    const canReadDebriefings = useHasPermissions(['READ.DEBRIEFINGS.ALL']);
    const isDebriefingEnabled = useIsFeatureEnabled(FeatureFlag.DEBRIEFING_MODULE);
    const showDebriefings = canReadDebriefings && isDebriefingEnabled;
    const isNew = useIsNewDesign();
    const { user } = useAuth();
    const canUpdate = useHasPermissions(TASK_UPDATE_ANY_PERMISSIONS);
    const canUpdateCreated = useHasPermissions(TASK_UPDATE_CREATED_PERMISSIONS);
    const isDraft = !!task.isDraft;
    // A draft is edited in its form, as a whole.
    const canEdit = !isDraft && canEditTaskInPlace(task, user?.id, { canUpdate, canUpdateCreated });
    const leaveDeleted = useLeaveDeletedEntity('/tasks');
    const draftActions = useDraftActions({
        draft: isDraft ? { type: 'task', id: task.id } : null,
        createRoute: '/task/new',
        onDeleted: leaveDeleted,
    });
    const { draft } = draftActions;
    // On a draft, the values it was saved without read as missing.
    const missing = (rows: Row[]) => withMissing(rows, draft?.missingFields);

    if (isLoading) {
        return <Loader />;
    }

    // The API layer toasts a failed switch. The new design shows the switch before the answer.
    const handleSwitchTaskStatus = async () => {
        const nextStatus =
            task.taskStatusTypeCode === TaskStatusType.DONE ? TaskStatusType.IN_PROGRESS : TaskStatusType.DONE;
        try {
            await switchTaskStatus({ id: `${task.id}`, nextStatus: isNew ? nextStatus : undefined }).unwrap();
        } catch (e) {
            console.error('onSubmit error: ', e);
        }
    };

    const matters = task.matters ?? [];
    const matterClientCombinations = [
        ...matters.map((matter) => ({
            contact: matter.clientParty,
            matterId: matter.id as number | null,
            matterName: matter.fullName as string | null,
        })),
        ...sortByField(
            (task.clientParties ?? [])
                .filter((contact) => !matters.some((matter) => matter.clientParty?.id === contact.id))
                .map((contact) => ({ contact, matterId: null, matterName: null })),
            'contact.name'
        ),
    ];

    const taskTypes = (task.taskTypeCodes ?? [])
        .map((code) => getClassifierText(classifiers['TASK_TYPE'], code))
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b))
        .join(', ');

    const headRows: Row[] = [
        { label: t('tasks.view-form.type'), content: taskTypes || '-', formLabels: [t('tasks.form.task-type')] },
        { label: t('tasks.form.task-name'), content: task.name || '-' },
        { label: t('tasks.form.description'), content: task.description || '-', multiline: true },
        {
            label: t('tasks.view-form.priority'),
            content: getClassifierText(classifiers['TASK_PRIORITY_TYPE'], task.taskPriorityTypeCode) || '-',
        },
        {
            label: t('tasks.view-form.status'),
            content: getClassifierText(classifiers['TASK_STATUS_TYPE'], task.taskStatusTypeCode) || '-',
        },
    ];
    const deadlineRow: Row = {
        label: t('tasks.view-form.deadline'),
        formLabels: [t('tasks.form.deadline-date')],
        content: task.deadlineDate ? (
            <span className={cn('text-body1', isDateEarlierThanToday(task.deadlineDate) && 'text-error')}>
                <FormattedDate value={task.deadlineDate} />
            </span>
        ) : (
            '-'
        ),
    };
    const assigneeRows: Row[] = [
        {
            label: t('tasks.form.assigned-to'),
            content: task.assignedToEmployees?.length ? getLinks('employee', task.assignedToEmployees) : '-',
        },
        {
            label: t('tasks.form.assigned-by'),
            content: task.assignedByEmployees?.length ? getLinks('employee', task.assignedByEmployees) : '-',
        },
    ];
    const officialRows: Row[] = [
        {
            label: isNew ? infoLabel('official', 'tasks.tooltips.official', 'justify-between') : t('official'),
            content: task.isOfficial ? t('yes') : t('no'),
        },
        ...(task.isOfficial
            ? [
                  {
                      label: isNew
                          ? infoLabel('official-reference-number', 'tasks.tooltips.official-reference-number')
                          : t('official-reference-number'),
                      content: task.officialReference || '-',
                  },
              ]
            : []),
    ];
    const generalRows: Row[] = [...headRows, deadlineRow, ...assigneeRows, ...officialRows];

    const connectionRows: Row[] = [
        {
            label: t('tasks.view-form.cases-clients'),
            content: matterClientCombinations.length ? (
                <div className="flex flex-col gap-1">
                    {matterClientCombinations.map(({ matterId, matterName, contact }, index) => (
                        <div
                            key={matterId != null ? `m-${matterId}` : (contact?.id ?? `i-${index}`)}
                            className="inline-flex flex-wrap gap-1"
                        >
                            {matterId && matterName ? (
                                <>
                                    <RouterLink className={LINK_CLASS} to={`/matter/${matterId}`}>
                                        {matterName}
                                    </RouterLink>
                                    {contact ? (
                                        <span>
                                            (<PartyName party={contact} isLink variant="readonly-other" />)
                                        </span>
                                    ) : null}
                                </>
                            ) : contact?.id ? (
                                <PartyName party={contact} isLink variant="readonly-other" />
                            ) : null}
                        </div>
                    ))}
                </div>
            ) : (
                '-'
            ),
        },
        ...(showAssignments
            ? [
                  {
                      label: t('tasks.view-form.connected-assignments'),
                      content: task.assignments?.length ? (
                          <div className="flex flex-col gap-1">
                              {task.assignments.map(({ id, name, matter }) => (
                                  <div key={id} className="inline-flex flex-wrap gap-1">
                                      <span>{name}</span>
                                      {matter ? (
                                          <RouterLink className={LINK_CLASS} to={`/matter/${matter.id}`}>
                                              ({matter.name})
                                          </RouterLink>
                                      ) : null}
                                  </div>
                              ))}
                          </div>
                      ) : (
                          '-'
                      ),
                  },
              ]
            : []),
        ...(showCorrespondence
            ? [
                  {
                      label: t('tasks.view-form.connected-correspondences'),
                      content: task.correspondence?.length ? (
                          <div className="flex flex-col gap-1">
                              {task.correspondence.map(({ id, fullName, dmsLink }) => (
                                  <div key={id} className="inline-flex items-center gap-1">
                                      <RouterLink className={LINK_CLASS} to={`/correspondence/${id}`}>
                                          {fullName}
                                      </RouterLink>
                                      {dmsLink ? <DmsLink link={dmsLink} isIconDisplayed label="" /> : null}
                                  </div>
                              ))}
                          </div>
                      ) : (
                          '-'
                      ),
                  },
              ]
            : []),
        ...(showDebriefings
            ? [
                  {
                      label: t('tasks.form.debriefings'),
                      content: task.debriefings?.length
                          ? getLinks(
                                'debriefing',
                                task.debriefings.map((debriefing) => ({
                                    id: debriefing.id,
                                    name: debriefingLabel(debriefing),
                                }))
                            )
                          : '-',
                  },
              ]
            : []),
    ];

    const taskEvent = task.taskEvent;
    const calendarRows: Row[] = taskEvent
        ? [
              { label: t('tasks.form.date-start'), content: <FormattedDate value={taskEvent.dateStart} /> },
              ...(taskEvent.isAllDayEvent
                  ? []
                  : [
                        {
                            label: t('tasks.form.time-start'),
                            content: taskEvent.dateStart ? formatTimeOfDay(taskEvent.dateStart) : '-',
                        },
                    ]),
              { label: t('tasks.form.date-end'), content: <FormattedDate value={taskEvent.dateEnd} /> },
              ...(taskEvent.isAllDayEvent
                  ? []
                  : [
                        {
                            label: t('tasks.form.time-end'),
                            content: taskEvent.dateEnd ? formatTimeOfDay(taskEvent.dateEnd) : '-',
                        },
                    ]),
              { label: t('tasks.form.all-day-meeting'), content: taskEvent.isAllDayEvent ? t('yes') : t('no') },
              {
                  label: t('tasks.form.meeting-organiser'),
                  content: taskEvent.inviteSender || task.organizerEmail || '-',
              },
              {
                  label: t('tasks.form.invite-colleagues'),
                  content: taskEvent.eventInvitees?.length
                      ? getLinks(
                            'employee',
                            taskEvent.eventInvitees.map((invitee) => ({
                                id: invitee.employeeId,
                                name: invitee.employeeName ?? '',
                            }))
                        )
                      : '-',
              },
              { label: t('tasks.form.location'), content: taskEvent.eventLocation || '-' },
              { label: t('tasks.form.event-description'), content: taskEvent.description || '-', multiline: true },
              {
                  label: t('tasks.form.event-reminder'),
                  content: taskEvent.reminder
                      ? getClassifierText(classifiers['EVENT_REMINDER_CODE'], taskEvent.reminder)
                      : '-',
              },
          ]
        : [];

    const dmsRows: Row[] = [
        {
            label: t('tasks.form.dms-link'),
            content: task.dmsData?.dmsLink ? <DmsLink link={task.dmsData.dmsLink} label={task.dmsData.dmsLink} /> : '-',
        },
        {
            label: t('tasks.form.dms-database'),
            content: task.dmsData?.dmsDatabase
                ? getClassifierText(classifiers['DMS_DATABASE'], task.dmsData.dmsDatabase)
                : '-',
        },
        { label: t('tasks.form.dms-number'), content: task.dmsData?.dmsNumber || '-' },
    ];

    const notesRows: Row[] = [
        {
            label: isNew ? infoLabel('tasks.notes', 'tasks.tooltips.notes') : t('tasks.notes'),
            content: task.notes || '-',
            multiline: true,
        },
    ];

    // The new design's cards list their form's fields in the form's order, under the form's labels and where the
    // form shows them, a multi-select's values as the chips the field draws, so a row and its field share a cell.
    // A row no field edits is `readOnly`: it follows the others and stays as text while its card edits.
    const newDesignRows = () => {
        const newGeneralRows: Row[] = [
            {
                label: t('tasks.form.task-type'),
                content: '-',
                // In the classifier's order, as the form's value is.
                chips: getOptionsFromClassifier<string, string>(classifiers['TASK_TYPE'])
                    .filter(({ value }) => (task.taskTypeCodes ?? []).includes(value))
                    .map(({ value, label }) => ({ key: value, label })),
            },
            { label: t('tasks.form.task-name'), content: task.name || '-' },
            { label: t('tasks.form.description'), content: task.description || '-', multiline: true },
            {
                label: t('tasks.form.priority'),
                content: getClassifierText(classifiers['TASK_PRIORITY_TYPE'], task.taskPriorityTypeCode) || '-',
            },
            ...officialRows,
            {
                label: t('tasks.view-form.status'),
                content: getClassifierText(classifiers['TASK_STATUS_TYPE'], task.taskStatusTypeCode) || '-',
                readOnly: true,
            },
        ];
        const newAssigneeRows: Row[] = [
            {
                label: t('tasks.form.assigned-to'),
                content: '-',
                chips: getLinkChips('employee', sortByField(task.assignedToEmployees ?? [], 'name')),
            },
            {
                label: t('tasks.form.assigned-by'),
                content: '-',
                chips: getLinkChips('employee', sortByField(task.assignedByEmployees ?? [], 'name')),
            },
        ];
        const newConnectionRows: Row[] = [
            {
                label: t('tasks.form.contact'),
                content: '-',
                chips: getLinkChips('contact', sortByField(task.clientParties ?? [], 'name')),
            },
            {
                label: t('tasks.form.cases'),
                content: '-',
                chips: getCaseChips(matters.toSorted((a, b) => a.id - b.id)),
            },
            {
                label: t('tasks.form.assignments'),
                content: '-',
                chips: (task.assignments ?? []).map(({ id, name }) => ({ key: id, label: name })),
                // The form offers assignments once a case is chosen, to who may read them.
                hidden: matters.length === 0 || !showAssignments,
            },
            {
                label: t('tasks.form.correspondence'),
                content: '-',
                chips: (task.correspondence ?? []).map(({ id, fullName, dmsLink }) => ({
                    key: id,
                    record: true,
                    label: <RecordLink className={LINK_CLASS} to={`/correspondence/${id}`} label={fullName} />,
                    adornment: dmsLink ? <DmsLink link={dmsLink} isIconDisplayed label="" /> : null,
                })),
                hidden: !showCorrespondence,
            },
            {
                label: t('tasks.form.debriefings'),
                content: '-',
                chips: (task.debriefings ?? []).map((debriefing) => ({
                    key: debriefing.id,
                    record: true,
                    label: (
                        <RecordLink
                            className={LINK_CLASS}
                            to={`/debriefing/${debriefing.id}`}
                            label={debriefingLabel(debriefing)}
                        />
                    ),
                })),
                hidden: !showDebriefings,
            },
        ];
        const eventTime = (value: Date | null) =>
            taskEvent && !taskEvent.isAllDayEvent && value ? formatTimeOfDay(value) : '-';
        const newCalendarRows: Row[] = [
            { label: t('tasks.form.has-deadline'), content: task.hasDeadline ? t('yes') : t('no') },
            {
                label: t('tasks.form.deadline-date'),
                content: task.deadlineDate ? (
                    <EllipsisTooltip>
                        <span className={cn(isDateEarlierThanToday(task.deadlineDate) && 'text-error')}>
                            <FormattedDate value={task.deadlineDate} />
                        </span>
                    </EllipsisTooltip>
                ) : (
                    '-'
                ),
                hidden: !task.hasDeadline,
            },
            { label: t('tasks.form.calendar-sync'), content: taskEvent ? t('yes') : t('no') },
            ...(taskEvent
                ? [
                      { label: t('tasks.form.date-start'), content: <FormattedDate value={taskEvent.dateStart} /> },
                      // The form keeps both time fields, empty and disabled, on an all-day event.
                      { label: t('tasks.form.time-start'), content: eventTime(taskEvent.dateStart) },
                      { label: t('tasks.form.date-end'), content: <FormattedDate value={taskEvent.dateEnd} /> },
                      { label: t('tasks.form.time-end'), content: eventTime(taskEvent.dateEnd) },
                      { label: t('tasks.form.all-day-meeting'), content: taskEvent.isAllDayEvent ? t('yes') : t('no') },
                      // Read only in the form too, where it keeps this place: the form ends with a row of its own,
                      // which a row drawn after the fields would fall under.
                      {
                          label: t('tasks.form.meeting-organiser'),
                          content: taskEvent.inviteSender || task.organizerEmail || '-',
                      },
                      {
                          label: infoLabel('tasks.form.invite-colleagues', 'tasks.form.invite-colleagues-tooltip'),
                          content: '-',
                          chips: getLinkChips(
                              'employee',
                              (taskEvent.eventInvitees ?? []).map((invitee) => ({
                                  id: invitee.employeeId,
                                  name: invitee.employeeName ?? '',
                              }))
                          ),
                      },
                      { label: t('tasks.form.location'), content: taskEvent.eventLocation || '-' },
                      {
                          label: t('tasks.form.event-description'),
                          content: taskEvent.description || '-',
                          multiline: true,
                      },
                      {
                          label: t('tasks.form.event-reminder'),
                          content: taskEvent.reminder
                              ? getClassifierText(classifiers['EVENT_REMINDER_CODE'], taskEvent.reminder)
                              : '-',
                      },
                  ]
                : []),
        ];
        const newDmsRows: Row[] = [
            {
                label: t('tasks.form.dms-link'),
                // One line at the value's size, as the link field shows it.
                content: task.dmsData?.dmsLink ? dmsLinkValue(task.dmsData.dmsLink) : '-',
            },
            ...dmsRows.slice(1).map((row) => ({ ...row, readOnly: true })),
        ];

        return {
            general: newGeneralRows,
            assignees: newAssigneeRows,
            connections: newConnectionRows,
            calendar: newCalendarRows,
            dms: newDmsRows,
        };
    };
    // Built for the new design alone, so classic never runs them.
    const cards = isNew ? newDesignRows() : null;

    const sectionCard = (id: TaskSectionId, titleId: string, data: Row[]) => (
        <TaskSection id={id} titleId={titleId} task={task} rows={data}>
            {(edit) => <GridItemList isNew titleId={titleId} data={missing(data)} button={edit} />}
        </TaskSection>
    );
    // Whom a draft is shared with heads the right column.
    const sharedWith = isDraft ? (
        <DraftActionsContext.Provider value={draftActions}>
            <DraftSharedWith />
            {draftActions.shareDialog}
        </DraftActionsContext.Provider>
    ) : null;

    return (
        <PaddedLayout>
            <PageHeader
                // A draft may be saved before it is named.
                title={task.name || t('new-entity.task')}
                showBreadcrumb
                onBack={goBack}
                buttons={
                    isDraft ? (
                        <div className="flex items-center gap-2">
                            <DraftRecordActions actions={draftActions} />
                        </div>
                    ) : !task.isAutomatic ? (
                        <div className="flex items-center gap-2">
                            {isNew ? null : (
                                <PermissionButton
                                    permissions={TASK_UPDATE_PERMISSIONS}
                                    variant="outlined"
                                    size="small"
                                    onClick={() => navigateWithReturn(`/task/${task.id}/edit`)}
                                >
                                    <FormattedMessage id="edit" />
                                </PermissionButton>
                            )}
                            <PermissionButton
                                permissions={TASK_UPDATE_PERMISSIONS}
                                variant="contained"
                                color="secondary"
                                size="small"
                                disabled={buttonsDisabled}
                                // In the new design it keeps its focus while the switch runs, already showing its next
                                // label, and dims itself: the kit dims only a natively disabled button.
                                focusableWhenDisabled={isNew}
                                className={isNew ? OFF_SECONDARY_CLASS : undefined}
                                onClick={() => {
                                    void handleSwitchTaskStatus();
                                }}
                            >
                                <FormattedMessage
                                    id={
                                        task.taskStatusTypeCode === TaskStatusType.DONE
                                            ? 'tasks.view-form.open-task'
                                            : 'tasks.view-form.mark-as-done'
                                    }
                                />
                            </PermissionButton>
                        </div>
                    ) : undefined
                }
            >
                <div className="mt-2 flex flex-wrap items-center gap-2 py-2">
                    {isDraft ? <DraftChip /> : null}
                    <StatusChip
                        type={task.taskStatusTypeCode === TaskStatusType.DONE ? 'neutral' : 'success'}
                        label={getClassifierText(classifiers['TASK_STATUS_TYPE'], task.taskStatusTypeCode)}
                    />
                    {task.taskPriorityTypeCode ? (
                        <StatusChip
                            type={getPriorityStatusChipType(task.taskPriorityTypeCode)}
                            label={getClassifierText(classifiers['TASK_PRIORITY_TYPE'], task.taskPriorityTypeCode)}
                        />
                    ) : null}
                    {task.dmsData?.dmsLink ? <DmsLink link={task.dmsData.dmsLink} isIconDisplayed /> : null}
                    {isNew && !isDraft ? <PinButton pin={recordPin('task', task.id, task.name)} /> : null}
                    {draft ? <DraftMeta draft={draft} /> : null}
                </div>
            </PageHeader>
            {draft ? (
                <div className="new:mb-(--card-gap) mb-6">
                    <DraftNotice actions={draftActions} />
                </div>
            ) : null}
            {cards ? (
                // One card per form section, each editing in place; the calendar card shows even without an event,
                // as switching sync on lives there.
                <SectionEditProvider canEdit={canEdit}>
                    <div className="grid grid-cols-1 items-start gap-(--card-gap) lg:grid-cols-2">
                        <div className="flex min-w-0 flex-col gap-(--card-gap)">
                            {sectionCard('general', 'tasks.form.general', cards.general)}
                            {sectionCard('assignees', 'tasks.form.assignees', cards.assignees)}
                            {sectionCard('notes', 'tasks.form.notes-header', notesRows)}
                        </div>
                        <div className="flex min-w-0 flex-col gap-(--card-gap)">
                            {sharedWith}
                            {sectionCard('connections', 'tasks.form.task-connections', cards.connections)}
                            {sectionCard('deadline-calendar', 'tasks.form.deadline-calendar', cards.calendar)}
                            {sectionCard('dms', 'tasks.form.dms-header', cards.dms)}
                        </div>
                    </div>
                </SectionEditProvider>
            ) : (
                <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-2">
                    <div className="flex flex-col gap-5">
                        <GridItemList titleId="tasks.form.general" size={12} data={missing(generalRows)} />
                        <GridItemList titleId="tasks.form.notes-header" size={12} data={missing(notesRows)} />
                    </div>
                    <div className="flex flex-col gap-5">
                        {sharedWith}
                        <GridItemList titleId="tasks.form.task-connections" size={12} data={missing(connectionRows)} />
                        {taskEvent ? (
                            <GridItemList
                                titleId="tasks.form.calendar-settings"
                                size={12}
                                data={missing(calendarRows)}
                            />
                        ) : null}
                        <GridItemList titleId="tasks.form.dms-header" size={12} data={missing(dmsRows)} />
                    </div>
                </div>
            )}
            <div className="mt-6">
                <AuditInfo
                    createdAt={task.createdAt ? new Date(task.createdAt) : undefined}
                    createdByEmployee={task.createdByEmployee}
                    lastModifiedAt={task.lastModifiedAt ? new Date(task.lastModifiedAt) : undefined}
                    changedByEmployee={task.lastModifiedByEmployee}
                />
            </div>
        </PaddedLayout>
    );
};

export default TaskPageView;
